/**
 * HomepageV3 editorial CSS custom properties.
 *
 * Maps every `:root` variable from `design-refs/source/assets/private-home.css`
 * onto a root wrapper, so CSS ported from the design (which references
 * `var(--night)`, `var(--cream)`, `var(--serif)`, etc.) resolves correctly when
 * scoped under the HomepageV3 tree.
 *
 * Font vars (`--serif`/`--sans`/`--mono`) match the source handoff stacks.
 *
 * Usage: spread `editorialRootVars` into an `sx`/`styled` block on the
 * HomepageV3 root element, or wrap with `EditorialRoot`.
 */
import { styled } from '@mui/material/styles';
import { FONTS } from 'components/HomepageV3/theme/tokens';

export const editorialRootVars = {
  // Dark surfaces
  '--night': '#0B292D',
  '--night-2': '#103A3E',
  '--night-deep': '#061819',
  // Light surfaces
  '--cream': '#FCF9F5',
  '--cream-2': '#F5EFE4',
  '--card': '#FFFFFF',
  '--edge': '#E8E1D6',
  // Accents
  '--gold': '#8C6A2C',
  '--champagne': '#E9D7B6',
  '--teal': '#01D1B8',
  '--teal-ink': '#0C6F64',
  '--wine': '#A33A4C',
  // Ink (text on cream)
  '--ink': '#221E19',
  '--ink-2': '#38332B',
  '--ink-3': '#514B40',
  // Text on dark
  '--c-text': 'rgba(255,255,255,0.96)',
  '--c-text-2': 'rgba(255,255,255,0.84)',
  '--c-text-3': 'rgba(255,255,255,0.70)',
  '--serif': FONTS.serif,
  '--sans': FONTS.sans,
  '--mono': FONTS.mono,
  // Page side padding
  '--pad': '64px',
} as const;

export const editorialScrollGlobalStyles = {
  body: {
    // PD-36047 — shared layout CSS sets body overflow to `auto !important`,
    // which makes sticky descendants bind to the wrong scroll container.
    overflow: 'visible !important',
  },
} as const;

/**
 * Root wrapper that publishes the editorial CSS custom properties and sets the
 * base cream surface + sans body font. Section components mount inside this.
 */
export const EditorialRoot = styled('div')({
  ...editorialRootVars,
  '*, *::before, *::after': {
    boxSizing: 'border-box',
  },
  width: '100%',
  // PD-35993/PD-36047 — contain horizontal overflow without creating a scroll
  // container that breaks the desktop sticky offering sidebar.
  maxWidth: '100vw',
  overflowX: 'hidden',
  '@supports (overflow: clip)': {
    overflowX: 'clip',
  },
  minHeight: '100vh',
  background: 'var(--cream)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  WebkitFontSmoothing: 'antialiased',
  boxSizing: 'border-box',
  '& [data-cy="editorial-nav"]': {
    '& a, & button, & input': {
      '&:focus-visible': {
        outline: '2px solid var(--champagne)',
        outlineOffset: '3px',
      },
    },
  },
  '& [data-cy="announcement-banner"]': {
    '@media (max-width: 640px)': {
      fontSize: '9px',
      letterSpacing: '0.1em',
      padding: '9px 14px',
      gap: '8px',
    },
  },
  '@media (max-width: 1080px)': {
    '--pad': '36px',
  },
  '@media (max-width: 640px)': {
    '--pad': '18px',
  },
});

export default EditorialRoot;
